import React from 'react';
import { Card, Text, Stack, Group, Badge, Grid } from '@mantine/core';
import { IconTarget, IconSword, IconShield } from '@tabler/icons-react';
import { PlayerStats } from 'types/analytics';

interface PlayerStatsCardProps {
    player: PlayerStats | null;
    showDetailed?: boolean;
}

export const PlayerStatsCard: React.FC<PlayerStatsCardProps> = ({ player, showDetailed = false }) => {
    if (!player) {
        return (
            <Card shadow="sm" padding="md" radius="md" withBorder>
                <Text c="dimmed" size="sm">Tilastot eivät ole saatavilla</Text>
            </Card>
        );
    }

    return (
        <Card shadow="sm" padding="md" radius="md" withBorder>
            <Stack gap="xs">
                <Group justify="space-between">
                    <Text fw={600} size="lg">{player.playerName}</Text>
                    <Badge color={player.team === 'CT' ? 'blue' : 'orange'} variant="light">
                        {player.team}
                    </Badge>
                </Group>

                <Grid gutter="xs">
                    <Grid.Col span={4}>
                        <Stack gap={4}>
                            <Text size="xs" c="dimmed">K/D</Text>
                            <Text fw={600} size="lg">{player.kd.toFixed(2)}</Text>
                            <Text size="xs" c="dimmed">
                                {player.kills}K / {player.deaths}D
                            </Text>
                        </Stack>
                    </Grid.Col>
                    <Grid.Col span={4}>
                        <Stack gap={4}>
                            <Text size="xs" c="dimmed">ADR</Text>
                            <Text fw={600} size="lg">{player.adr.toFixed(1)}</Text>
                            <Text size="xs" c="dimmed">Avg Damage</Text>
                        </Stack>
                    </Grid.Col>
                    <Grid.Col span={4}>
                        <Stack gap={4}>
                            <Text size="xs" c="dimmed">MVP</Text>
                            <Text fw={600} size="lg">{player.mvpScore.toFixed(1)}</Text>
                            <Text size="xs" c="dimmed">Score</Text>
                        </Stack>
                    </Grid.Col>
                </Grid>

                {showDetailed && (
                    <>
                        <Group gap="md" mt="xs">
                            <Group gap={4}>
                                <IconSword size={16} />
                                <Text size="sm">Assists: {player.assists}</Text>
                            </Group>
                            <Group gap={4}>
                                <IconTarget size={16} />
                                <Text size="sm">HS%: {player.headshotPercentage.toFixed(1)}%</Text>
                            </Group>
                            <Group gap={4}>
                                <IconShield size={16} />
                                <Text size="sm">KAST: {player.kast}%</Text>
                            </Group>
                        </Group>

                        <Grid gutter="xs" mt="xs">
                            <Grid.Col span={6}>
                                <Text size="xs" c="dimmed">K/D Diff</Text>
                                <Text size="sm" fw={500}>{player.kdDiff > 0 ? '+' : ''}{player.kdDiff}</Text>
                            </Grid.Col>
                            <Grid.Col span={6}>
                                <Text size="xs" c="dimmed">Impact</Text>
                                <Text size="sm" fw={500}>{player.impactRating.toFixed(2)}</Text>
                            </Grid.Col>
                            <Grid.Col span={6}>
                                <Text size="xs" c="dimmed">First Kills</Text>
                                <Text size="sm" fw={500}>{player.firstKills}</Text>
                            </Grid.Col>
                            <Grid.Col span={6}>
                                <Text size="xs" c="dimmed">Opening Duel</Text>
                                <Text size="sm" fw={500}>{player.openingDuelSuccess.toFixed(0)}%</Text>
                            </Grid.Col>
                            <Grid.Col span={6}>
                                <Text size="xs" c="dimmed">Clutches</Text>
                                <Text size="sm" fw={500}>
                                    {player.clutchesWon}W / {player.clutchesLost}L ({player.clutchSuccess.toFixed(0)}%)
                                </Text>
                            </Grid.Col>
                            <Grid.Col span={6}>
                                <Text size="xs" c="dimmed">Trade Kills</Text>
                                <Text size="sm" fw={500}>{player.tradeKills}</Text>
                            </Grid.Col>
                        </Grid>
                    </>
                )}
            </Stack>
        </Card>
    );
};

